import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../shared/Card';
import axios from 'axios';
import { useSelector } from 'react-redux';

const API_URL = import.meta.env.VITE_API_URL;

const UserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const { admin, token } = useSelector((state) => state.auth);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        const response = await axios.get(`${API_URL}/api/users/${userId}`, config);
        const data = response.data;
        setUser(data);
      } catch (err) {
        console.error('Error fetching user:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchUserData();
    }
  }, [userId, token]);

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num || 0);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price || 0);
  };

  const handleInternalStatusToggle = async () => {
    if (!user || !admin || admin.role !== 'admin') return;
    
    try {
      setUpdating(true);
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      const newInternalStatus = !(user.user.isInternal || false);
      const response = await axios.patch(
        `${API_URL}/api/users/${userId}/internal-status`,
        { isInternal: newInternalStatus },
        config
      );
      
      if (response.data.success) {
        setUser(prev => ({
          ...prev,
          user: {
            ...prev.user,
            isInternal: newInternalStatus
          }
        }));
      }
    } catch (error) {
      console.error('Error updating internal status:', error);
      setError('Failed to update internal status');
    } finally {
      setUpdating(false);
    }
  };

  // Function to calculate cost for Fullstack projects
  const calculateFullstackCost = (inputTokens, outputTokens) => {
    const input = inputTokens || 0;
    const output = outputTokens || 0;
    return (input * 0.000003) + (output * 0.000015);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading user details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="text-center text-red-600 py-4">
          {error}
        </div>
      </Card>
    );
  }

  if (!user) {
    return (
      <Card>
        <div className="text-center text-gray-600 py-4">
          User not found
        </div>
      </Card>
    );
  }

  // Calculate total price including Fullstack project costs
  const totalPrice = user.projects.reduce((sum, project) => {
    let projectCost = Number(project.price) || 0;
    
    // Add calculated cost for Fullstack projects
    if (project.type === 'Fullstack') {
      const fullstackCost = calculateFullstackCost(
        project.tokens?.input_tokens,
        project.tokens?.output_tokens
      );
      projectCost += fullstackCost;
    }
    
    return sum + projectCost;
  }, 0);

  const totalInputTokens = user.projects.reduce((sum, project) => sum + (project.tokens?.input_tokens || 0), 0);
  const totalOutputTokens = user.projects.reduce((sum, project) => sum + (project.tokens?.output_tokens || 0), 0);

  return (
    <div className="space-y-6 pt-16">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/users')}
          className="flex items-center text-indigo-600 hover:text-indigo-800"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Users
        </button>
      </div>

      <Card>
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-gray-900">User Details</h1>

          <div className="border-b border-gray-200 pb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">User Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">ID:</p>
                <p className="text-sm text-gray-900 mt-1">{user.user._id}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Full Name</p>
                <p className="text-sm text-gray-900 mt-1">{user.user.name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Email</p>
                <p className="text-sm text-gray-900 mt-1">{user.user.email}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Created At</p>
                <p className="text-sm text-gray-900 mt-1">{formatDate(user.user.createdAt)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Total Price</p>
                <p className="text-sm text-gray-900 mt-1">{formatPrice(totalPrice)}</p>
              </div>
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Total Input Tokens</p>
                    <p className="text-sm text-gray-900 mt-1">{formatNumber(totalInputTokens)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Total Output Tokens</p>
                    <p className="text-sm text-gray-900 mt-1">{formatNumber(totalOutputTokens)}</p>
                  </div>
                </div>
              </div>
              {admin && admin.role === 'admin' && (
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2">Internal User</p>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handleInternalStatusToggle}
                      disabled={updating}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                        (user.user.isInternal || false) ? 'bg-indigo-600' : 'bg-gray-200'
                      } ${updating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          (user.user.isInternal || false) ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className="text-sm text-gray-700">
                      {(user.user.isInternal || false) ? 'Internal' : 'External'}
                    </span>
                    {updating && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Projects <span className="text-gray-600">({user.projects.length})</span>
            </h2>
            {user.projects && user.projects.length > 0 ? (
              <div className="space-y-4">
                {user.projects.map(project => {
                  // Calculate additional cost for Fullstack projects
                  const fullstackCost = project.type === 'Fullstack' 
                    ? calculateFullstackCost(project.tokens?.input_tokens, project.tokens?.output_tokens)
                    : 0;
                  const totalProjectCost = (Number(project.price) || 0) + fullstackCost;

                  return (
                    <div key={project._id} className="border border-gray-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-500">Project ID</p>
                          <p className="text-sm text-gray-900 mt-1">{project._id}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Name</p>
                          <p className="text-sm text-gray-900 mt-1">{project.name}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Prompt</p>
                          <p className="text-sm text-gray-900 mt-1">{project.prompt}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Type</p>
                          <p className="text-sm text-gray-900 mt-1">{project.type}</p>
                        </div>
                      </div>
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-500">Input Tokens</p>
                          <p className="text-sm text-gray-900 mt-1">{formatNumber(project.tokens?.input_tokens)}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Output Tokens</p>
                          <p className="text-sm text-gray-900 mt-1">{formatNumber(project.tokens?.output_tokens)}</p>
                        </div>
                      </div>
                      <div className="mt-4">
                        <p className="text-sm font-medium text-gray-500">Price</p>
                        <p className="text-sm text-gray-900 mt-1">{formatPrice(totalProjectCost)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No projects found</p>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default UserDetailPage;